import React, { useState } from "react";
import * as XLSX from "xlsx";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { HistoryIcon, FileSearchIcon, FileSpreadsheetIcon } from "lucide-react";
import { toast } from "sonner";

const fmtH = (s) => (s ? new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDH = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const fmtDur = (a, b) => {
  if (!a || !b) return "";
  const min = Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}min`;
};
const hojeLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : "—");
// Período local (00:00 do 1º dia → 24:00 do último) convertido para ISO UTC, que é como o backend grava chegou_em
const limitesPeriodo = (ymdDe, ymdAte) => {
  const [y1, m1, d1] = ymdDe.split("-").map(Number);
  const [y2, m2, d2] = ymdAte.split("-").map(Number);
  return { de: new Date(y1, m1 - 1, d1, 0, 0, 0).toISOString(), ate: new Date(y2, m2 - 1, d2 + 1, 0, 0, 0).toISOString() };
};

const ETAPA_CFG = {
  CONCLUIDO: { txt: "FEITO", cls: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40" },
  EM_ANDAMENTO: { txt: "EM ANDAMENTO", cls: "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40 animate-pulse" },
  AGUARDANDO: { txt: "AGUARDANDO", cls: "bg-white/5 text-white/60 border-white/20" },
  CANCELADO: { txt: "CANCELADO", cls: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40" },
  NAO_REALIZADO: { txt: "NÃO FEZ", cls: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40" },
};
const SITUACAO = {
  AGUARDANDO: { txt: "NA FILA (FORA)", cls: "text-[#7DD3FC] border-[#7DD3FC]/40" },
  EM_OPERACAO: { txt: "NA BASE", cls: "text-[#FFD60A] border-[#FFD60A]/40" },
  ATENDIDO: { txt: "SAIU", cls: "text-[#34C759] border-[#34C759]/40" },
  CANCELADO: { txt: "CANCELADO", cls: "text-[#FF3B30] border-[#FF3B30]/40" },
};

const EtapaCell = ({ status, motivo }) => {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const c = ETAPA_CFG[status] || { txt: status, cls: "border-border" };
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge className={`rounded-none text-[9px] font-black border ${c.cls}`}>{c.txt}</Badge>
      {motivo && <span className="text-[10px] text-[#FF3B30]/80 max-w-[220px] leading-tight">{motivo}</span>}
    </div>
  );
};

// Fim da permanência: saída registrada; se cancelado/atendido sem saída → quando foi finalizado; senão ainda em curso (agora)
const fimPermanencia = (o) => o.hora_saida || (["ATENDIDO", "CANCELADO"].includes(o.status) ? o.finalizado_em : null);
const emCurso = (o) => !fimPermanencia(o);

const ETAPAS_EXPORT = [
  ["DESCARREGAR_CHEIOS", "Descarregar cheios"],
  ["CARREGAR_VAZIOS", "Carregar vazios"],
  ["DESCARREGAR_BACKLOAD", "Descarregar backload"],
  ["CARREGAR_LOAD", "Carregar load"],
  ["CARRETA_VAZIA_STANDBY", "Carreta vazia / Standby"],
];

/** Monta as linhas do Excel a partir do relatório já gerado (frota, placas, chegada, entrada, saída, tempos e etapas). */
export const linhasExcel = (ops) => ops.map((o) => {
  const fins = o.finalidades || [o.finalidade];
  const linha = {
    "Chegada": fmtDH(o.chegou_em),
    "Frota": o.frota || "",
    "Placa Cavalo": o.placa_cavalo || "",
    "Placa Carreta": o.placa_carreta || "",
    "Motorista": o.nome || "",
    "Empresa": o.empresa || "",
    "Entrada": fmtDH(o.hora_entrada || o.entrou_em),
    "Saída": fmtDH(o.hora_saida),
    "Espera na fila": fmtDur(o.chegou_em, o.hora_entrada || o.entrou_em),
    "Tempo na base": fmtDur(o.hora_entrada || o.entrou_em, fimPermanencia(o) || new Date().toISOString()),
    "Permanência total desde a chegada": fmtDur(o.chegou_em, fimPermanencia(o) || new Date().toISOString()) + (emCurso(o) ? " (em curso)" : ""),
  };
  ETAPAS_EXPORT.forEach(([k, label]) => {
    const st = fins.includes(k) ? (o.etapas?.[k] || "AGUARDANDO") : null;
    const motivo = o.cancelamentos?.[k]?.motivo || (k === "CARREGAR_VAZIOS" ? o.vazios_cancelado_motivo : null);
    linha[label] = st ? (ETAPA_CFG[st]?.txt || st) + (motivo ? ` — ${motivo}` : "") : "";
  });
  linha["Destino (load)"] = o.destino || "";
  linha["Situação"] = (SITUACAO[o.status] || { txt: o.status }).txt;
  linha["Entrada por"] = o.entrou_por || "";
  linha["Etapa por"] = o.etapa_atualizada_por || "";
  linha["Saída/Fim por"] = o.usuario_saida || o.finalizado_por || "";
  return linha;
});

const exportarExcel = (ops, de, ate) => {
  const linhas = linhasExcel(ops);
  const ws = XLSX.utils.json_to_sheet(linhas);
  ws["!cols"] = Object.keys(linhas[0]).map((k) => ({ wch: Math.max(12, Math.min(40, Math.max(k.length, ...linhas.map((l) => String(l[k] || "").length)) + 2)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Veículos");
  XLSX.writeFile(wb, `relatorio_veiculos_${de}_a_${ate}.xlsx`);
};

/** Relatório sob demanda: escolhe o período (de/até) e clica GERAR → lista o que cada carreta fez
 *  (descarregou, carregou, cancelou e por quê). Usado na Portaria e na Balança. Nada é carregado automaticamente. */
export function HistoricoVeiculos() {
  const [de, setDe] = useState(hojeLocal());
  const [ate, setAte] = useState(hojeLocal());
  const [ops, setOps] = useState(null);      // null = ainda não gerou
  const [loading, setLoading] = useState(false);

  const gerar = async () => {
    if (!de || !ate) return toast.error("Informe o período (de / até)");
    if (de > ate) return toast.error("A data inicial não pode ser maior que a final");
    setLoading(true);
    try {
      const { data } = await api.get("/portaria/operacoes", { params: limitesPeriodo(de, ate) });
      setOps(data);
      if (!data.length) toast.info("Nenhum veículo no período selecionado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const feitos = (ops || []).filter((o) => o.status === "ATENDIDO").length;
  const cancelados = (ops || []).filter((o) => o.status === "CANCELADO"
    || Object.values(o.etapas || {}).includes("CANCELADO")).length;
  const varioDias = de !== ate;

  return (
    <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto" data-testid="historico-veiculos">
      <div className="p-4 border-b border-border flex items-end gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF9F0A] flex items-center gap-2 mr-2 self-center">
          <HistoryIcon className="w-4 h-4" /> Relatório de Veículos
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">De</Label>
          <Input type="date" value={de} onChange={(e) => setDe(e.target.value)} data-testid="historico-veiculos-de"
                 className="mt-1 rounded-none bg-[#0A0A0C] h-9 w-40 text-xs font-mono border-border" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Até</Label>
          <Input type="date" value={ate} onChange={(e) => setAte(e.target.value)} data-testid="historico-veiculos-ate"
                 className="mt-1 rounded-none bg-[#0A0A0C] h-9 w-40 text-xs font-mono border-border" />
        </div>
        <Button onClick={gerar} disabled={loading} data-testid="historico-veiculos-gerar"
                className="rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 h-9 text-[11px] font-bold uppercase tracking-wider">
          <FileSearchIcon className="w-4 h-4 mr-1" /> {loading ? "Gerando..." : "Gerar relatório"}
        </Button>
        {ops && ops.length > 0 && (
          <Button onClick={() => exportarExcel(ops, de, ate)} variant="outline" data-testid="historico-veiculos-excel"
                  className="rounded-none border-[#34C759] text-[#34C759] hover:bg-[#34C759]/10 h-9 text-[11px] font-bold uppercase tracking-wider">
            <FileSpreadsheetIcon className="w-4 h-4 mr-1" /> Exportar Excel
          </Button>
        )}
        {ops && (
          <div className="ml-auto flex gap-3 text-[10px] uppercase tracking-wider font-mono self-center" data-testid="historico-veiculos-resumo">
            <span className="text-white/60">{ops.length} veículo(s)</span>
            <span className="text-[#34C759]">{feitos} saíram</span>
            <span className="text-[#FF3B30]">{cancelados} c/ cancelamento</span>
          </div>
        )}
      </div>
      {ops === null ? (
        <div className="px-4 py-8 text-center text-muted-foreground text-xs" data-testid="historico-veiculos-vazio">
          Selecione o período e clique em <b className="text-[#FF9F0A]">Gerar relatório</b> para ver o que cada carreta fez (descarregou, carregou, cancelou e por quê).
        </div>
      ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="px-4 py-2">Chegou</th>
            <th className="px-4 py-2">Placas</th>
            <th className="px-4 py-2">Motorista</th>
            <th className="px-4 py-2">Empresa</th>
            <th className="px-4 py-2">Entrou</th>
            <th className="px-4 py-2">Saiu</th>
            <th className="px-4 py-2">Permanência (desde a chegada)</th>
            <th className="px-4 py-2">Descarregar cheios</th>
            <th className="px-4 py-2">Carregar vazios</th>
            <th className="px-4 py-2">Descarregar backload</th>
            <th className="px-4 py-2">Carregar load</th>
            <th className="px-4 py-2">Carreta vazia / Standby</th>
            <th className="px-4 py-2">Situação</th>
            <th className="px-4 py-2">Por</th>
          </tr>
        </thead>
        <tbody>
          {ops.length === 0 && (
            <tr><td colSpan={14} className="px-4 py-8 text-center text-muted-foreground text-xs">Nenhum veículo no período.</td></tr>
          )}
          {ops.map((o) => {
            const sit = SITUACAO[o.status] || { txt: o.status, cls: "border-border" };
            const fins = o.finalidades || [o.finalidade];
            return (
              <tr key={o.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`historico-veiculo-${o.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{varioDias && <span className="text-muted-foreground">{fmtD(o.chegou_em)} </span>}{fmtH(o.chegou_em)}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-[#FFD60A]">{o.placa_cavalo || "—"}{o.placa_carreta ? ` / ${o.placa_carreta}` : ""}{o.frota && <span className="text-[#00E5FF]"> · FROTA {o.frota}</span>}</td>
                <td className="px-4 py-2 font-semibold text-white">{o.nome}</td>
                <td className="px-4 py-2 text-xs">{o.empresa || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs text-[#34C759]">{fmtH(o.hora_entrada || o.entrou_em)}</td>
                <td className="px-4 py-2 font-mono text-xs text-[#FF9F0A]">{fmtH(o.hora_saida)}</td>
                <td className="px-4 py-2 font-mono text-xs" data-testid={`historico-permanencia-${o.id}`}>
                  <span className="text-[#FFD60A] font-bold">{fmtDur(o.chegou_em, fimPermanencia(o) || new Date().toISOString()) || "—"}</span>
                  {emCurso(o) && <span className="text-muted-foreground"> (em curso)</span>}
                  {(o.hora_entrada || o.entrou_em) && (
                    <div className="text-[10px] text-muted-foreground leading-tight">fila {fmtDur(o.chegou_em, o.hora_entrada || o.entrou_em) || "—"} · base {fmtDur(o.hora_entrada || o.entrou_em, fimPermanencia(o) || new Date().toISOString()) || "—"}</div>
                  )}
                </td>
                <td className="px-4 py-2"><EtapaCell status={fins.includes("DESCARREGAR_CHEIOS") ? o.etapas?.DESCARREGAR_CHEIOS || "AGUARDANDO" : null} motivo={o.cancelamentos?.DESCARREGAR_CHEIOS?.motivo} /></td>
                <td className="px-4 py-2"><EtapaCell status={fins.includes("CARREGAR_VAZIOS") ? o.etapas?.CARREGAR_VAZIOS || "AGUARDANDO" : null} motivo={o.cancelamentos?.CARREGAR_VAZIOS?.motivo || o.vazios_cancelado_motivo} /></td>
                <td className="px-4 py-2"><EtapaCell status={fins.includes("DESCARREGAR_BACKLOAD") ? o.etapas?.DESCARREGAR_BACKLOAD || "AGUARDANDO" : null} motivo={o.cancelamentos?.DESCARREGAR_BACKLOAD?.motivo} /></td>
                <td className="px-4 py-2">
                  <EtapaCell status={fins.includes("CARREGAR_LOAD") ? o.etapas?.CARREGAR_LOAD || "AGUARDANDO" : null} motivo={o.cancelamentos?.CARREGAR_LOAD?.motivo} />
                  {o.destino && <div className="text-[10px] text-[#34C759] font-mono mt-0.5">→ {o.destino}</div>}
                </td>
                <td className="px-4 py-2"><EtapaCell status={fins.includes("CARRETA_VAZIA_STANDBY") ? o.etapas?.CARRETA_VAZIA_STANDBY || "AGUARDANDO" : null} motivo={o.cancelamentos?.CARRETA_VAZIA_STANDBY?.motivo} /></td>
                <td className="px-4 py-2"><Badge className={`rounded-none text-[9px] font-black border bg-transparent ${sit.cls}`}>{sit.txt}</Badge></td>
                <td className="px-4 py-2 text-[10px] text-muted-foreground leading-tight">
                  {o.entrou_por && <div>Entrada: {o.entrou_por}</div>}
                  {o.etapa_atualizada_por && <div>Etapa: {o.etapa_atualizada_por}</div>}
                  {(o.usuario_saida || o.finalizado_por) && <div>Saída/Fim: {o.usuario_saida || o.finalizado_por}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </Card>
  );
}
